import axios from "axios";
import { useEffect, useState } from "react";
import { Nav, Tab } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FaLock, FaMobileAlt, FaUser } from "react-icons/fa";
import config from "../../../../config.json";
import { updateUserData, userProfile } from "../../../services/AuthService";
import { getAuthToken } from "../../../utils/authToken";
import TwoFactorSettings from "../AppsMenu/AppProfile/TwoFactorSettings";

export default function FranchiseProfile() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("personal");
  const [user, setUser] = useState(null);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneWhatsapp, setPhoneWhatsapp] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadUser = async () => {
    try {
      const userData = await userProfile();
      setUser(userData);
      setTwoFactorEnabled(Boolean(userData?.twoFactorEnabled));
      setFirstName(userData?.firstName || "");
      setLastName(userData?.lastName || "");
      setPhone(userData?.phone || "");
      setPhoneWhatsapp(userData?.phoneWhatsapp || "");
    } catch (err) {
      console.error(err);
      toast.error(t("errorUserDataUpdate"));
    }
  };

  useEffect(() => {
    void loadUser();
  }, []);

  const handleUpdateUserData = async () => {
    try {
      const message = await updateUserData({
        firstName: firstName || user?.firstName,
        lastName: lastName || user?.lastName,
        phone: phone || user?.phone,
        phoneWhatsapp:
          phoneWhatsapp !== undefined ? phoneWhatsapp : user?.phoneWhatsapp,
      });

      if (message === "updated") {
        toast.success(t("userDataUpdated"));
        await loadUser();
      } else {
        toast.error(t("errorUserDataUpdate"));
      }
    } catch (err) {
      console.error(err);
      toast.error(t("errorUserDataUpdate"));
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();

    if (!oldPassword || !password || !confirmPassword) {
      setError(
        t("passwordFieldsRequired", {
          defaultValue: "All fields are required.",
        })
      );
      return;
    }

    if (password !== confirmPassword) {
      setError(
        t("passwordMismatch", {
          defaultValue: "The passwords do not match.",
        })
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();
      const response = await axios.put(
        `${config.dev}/user/updatepassword/${user._id}`,
        { oldPassword, password, confirmPassword },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      toast.success(response.data.message);
      setOldPassword("");
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        t("passwordUpdateError", { defaultValue: "Update password error" });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="row">
      <div className="col-xl-12">
        <div className="card">
          <div className="card-body">
            <h4 className="mb-1">{t("franchiseProfileTitle")}</h4>
            <p className="text-muted mb-4">{t("franchiseProfileSubtitle")}</p>

            <Tab.Container
              activeKey={activeTab}
              onSelect={(key) => setActiveTab(key)}
            >
              <Nav as="ul" className="nav nav-tabs">
                <Nav.Item as="li">
                  <Nav.Link eventKey="personal">
                    <FaUser style={{ marginRight: 8 }} />
                    {t("personalInfo")}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item as="li">
                  <Nav.Link eventKey="password">
                    <FaLock style={{ marginRight: 8 }} />
                    {t("password")}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item as="li">
                  <Nav.Link eventKey="twoFactor">
                    <FaMobileAlt style={{ marginRight: 8 }} />
                    {t("twoFactorAuthShort", { defaultValue: "2FA" })}
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              <Tab.Content className="pt-4">
                <Tab.Pane eventKey="personal">
                  <div className="mb-3">
                    <label className="form-label text-primary">
                      {t("email")}
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      value={user?.email || ""}
                      disabled
                      style={{ backgroundColor: "#e9ecef" }}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label text-primary">
                      {t("firstname")}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label text-primary">
                      {t("lastname")}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label text-primary">
                      {t("phone")}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label text-primary">
                      {t("phoneWhatsapp")}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={phoneWhatsapp || ""}
                      onChange={(e) => setPhoneWhatsapp(e.target.value)}
                    />
                  </div>

                  <div className="text-end">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleUpdateUserData}
                    >
                      {t("submitChanges")}
                    </button>
                  </div>
                </Tab.Pane>

                <Tab.Pane eventKey="password">
                  <form onSubmit={handleUpdatePassword}>
                    {error ? (
                      <div className="alert alert-danger">{error}</div>
                    ) : null}

                    <div className="mb-3">
                      <label className="form-label">{t("oldPwd")}</label>
                      <input
                        type="password"
                        className="form-control"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">{t("newPwd")}</label>
                      <input
                        type="password"
                        className="form-control"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">{t("confirmPwd")}</label>
                      <input
                        type="password"
                        className="form-control"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                    </div>

                    <div className="text-end">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading}
                      >
                        {loading ? t("updating") : t("submitChanges")}
                      </button>
                    </div>
                  </form>
                </Tab.Pane>

                <Tab.Pane eventKey="twoFactor">
                  <TwoFactorSettings
                    twoFactorEnabled={twoFactorEnabled}
                    onEnabledChange={setTwoFactorEnabled}
                  />
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </div>
        </div>
      </div>
    </div>
  );
}